import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import api from '../../lib/api';
import { 
  Download, 
  Eye, 
  Loader2, 
  Calendar, 
  MapPin, 
  Users, 
  Clock,
  Image as ImageIcon,
  Link as LinkIcon,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Copy,
  Trash2,
  ToggleLeft,
  ToggleRight,
  ListMusic
} from 'lucide-react';

export default function ImportEventPage() {
  const [url, setUrl] = useState('');
  const [editionId, setEditionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [redirects, setRedirects] = useState([]);
  const [editions, setEditions] = useState([]);
  const [loadingRedirects, setLoadingRedirects] = useState(false);

  // Import Agenda state
  const [agendaUrl, setAgendaUrl] = useState('');
  const [agendaEventId, setAgendaEventId] = useState('');
  const [agendaLoading, setAgendaLoading] = useState(false);
  const [agendaPreview, setAgendaPreview] = useState(null);
  const [agendaResult, setAgendaResult] = useState(null);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [editionsRes, eventsRes] = await Promise.all([
          api.get('/editions'),
          api.get('/events', { params: { include_drafts: true } }),
        ]);
        setEditions(editionsRes.data);
        setEvents(eventsRes.data);
      } catch (error) {
        console.error('Failed to load data:', error);
      }
    };
    loadData();
    loadRedirects();
  }, []);

  const loadRedirects = async () => {
    setLoadingRedirects(true);
    try {
      const response = await api.get('/import/redirects');
      setRedirects(response.data);
    } catch (error) {
      console.error('Failed to load redirects:', error);
    } finally {
      setLoadingRedirects(false);
    }
  };

  const handlePreview = async () => {
    if (!url) {
      toast.error('Inserisci un URL');
      return;
    }

    setLoading(true);
    setPreview(null);
    setImportResult(null);

    try {
      const response = await api.post('/import/preview', { url, edition_id: editionId || null });
      setPreview(response.data);
      toast.success('Anteprima generata');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'analisi della pagina');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!url) {
      toast.error('Inserisci un URL');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/import/execute', { url, edition_id: editionId || null });
      setImportResult(response.data);
      toast.success('Evento importato con successo!');
      loadRedirects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'import');
    } finally {
      setLoading(false);
    }
  };

  const copyPreviewLink = () => {
    if (importResult?.preview_url) {
      const fullUrl = `${window.location.origin}${importResult.preview_url}`;
      navigator.clipboard.writeText(fullUrl);
      toast.success('Link copiato negli appunti');
    }
  };

  const toggleRedirect = async (redirectId) => {
    try {
      await api.put(`/import/redirects/${redirectId}/toggle`);
      loadRedirects();
      toast.success('Redirect aggiornato');
    } catch (error) {
      toast.error('Errore durante l\'aggiornamento');
    }
  };

  const deleteRedirect = async (redirectId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo redirect?')) return;
    
    try {
      await api.delete(`/import/redirects/${redirectId}`);
      loadRedirects();
      toast.success('Redirect eliminato');
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  // ─── Import Agenda handlers ───
  const handleAgendaPreview = async () => {
    if (!agendaUrl || !agendaEventId) {
      toast.error('Seleziona un evento e inserisci un URL');
      return;
    }
    setAgendaLoading(true);
    setAgendaPreview(null);
    setAgendaResult(null);
    try {
      const res = await api.post('/import/agenda/preview', { url: agendaUrl, event_id: agendaEventId });
      setAgendaPreview(res.data);
      toast.success('Anteprima agenda generata');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'analisi');
    } finally {
      setAgendaLoading(false);
    }
  };

  const handleAgendaImport = async () => {
    if (!agendaUrl || !agendaEventId) {
      toast.error('Seleziona un evento e inserisci un URL');
      return;
    }
    setAgendaLoading(true);
    try {
      const res = await api.post('/import/agenda', { url: agendaUrl, event_id: agendaEventId });
      setAgendaResult(res.data);
      setAgendaPreview(null);
      toast.success(`Agenda importata: ${res.data.sessions_created} sessioni create`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'import dell\'agenda');
    } finally {
      setAgendaLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="import-event-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-white">Import Evento</h1>
            <p className="text-gray-400">Importa un evento da una URL esterna</p>
          </div>
        </div>

        <Tabs defaultValue="import" className="w-full">
          <TabsList className="bg-gray-800">
            <TabsTrigger value="import" className="data-[state=active]:bg-primary">
              <Download className="w-4 h-4 mr-2" />
              Import Evento
            </TabsTrigger>
            <TabsTrigger value="agenda" className="data-[state=active]:bg-primary">
              <ListMusic className="w-4 h-4 mr-2" />
              Import Agenda
            </TabsTrigger>
            <TabsTrigger value="redirects" className="data-[state=active]:bg-primary">
              <LinkIcon className="w-4 h-4 mr-2" />
              Redirect ({redirects.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="import" className="space-y-6 mt-6">
            {/* URL Input */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">URL Sorgente</CardTitle>
                <CardDescription>
                  Inserisci l'URL della pagina evento da importare (es. https://startupitaliaopensummit.eu/2025/winter-2025/)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <Input
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://startupitaliaopensummit.eu/2025/..."
                    className="flex-1 bg-gray-900 border-gray-600 text-white"
                    data-testid="import-url-input"
                  />
                  <select
                    value={editionId}
                    onChange={(e) => setEditionId(e.target.value)}
                    className="bg-gray-900 border border-gray-600 rounded-md px-3 text-white"
                  >
                    <option value="">Seleziona Edizione (opzionale)</option>
                    {editions.map((edition) => (
                      <option key={edition.id} value={edition.id}>
                        {edition.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handlePreview}
                    disabled={loading || !url}
                    variant="outline"
                    className="border-gray-600"
                    data-testid="preview-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                    Anteprima
                  </Button>
                  <Button
                    onClick={handleImport}
                    disabled={loading || !url}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="import-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    Importa come Bozza
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Import Result */}
            {importResult && (
              <Card className="bg-green-900/20 border-green-700">
                <CardHeader>
                  <CardTitle className="text-green-400 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Import Completato
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-gray-800 rounded-lg">
                      <p className="text-2xl font-bold text-white">{importResult.speakers_created}</p>
                      <p className="text-sm text-gray-400">Speaker creati</p>
                    </div>
                    <div className="text-center p-3 bg-gray-800 rounded-lg">
                      <p className="text-2xl font-bold text-white">{importResult.sessions_created}</p>
                      <p className="text-sm text-gray-400">Sessioni create</p>
                    </div>
                    <div className="text-center p-3 bg-gray-800 rounded-lg">
                      <p className="text-2xl font-bold text-white">{importResult.sponsors_imported}</p>
                      <p className="text-sm text-gray-400">Sponsor importati</p>
                    </div>
                    <div className="text-center p-3 bg-gray-800 rounded-lg">
                      <p className="text-2xl font-bold text-white">{importResult.redirect_created ? '1' : '0'}</p>
                      <p className="text-sm text-gray-400">Redirect creato</p>
                    </div>
                  </div>

                  <div className="p-4 bg-gray-800 rounded-lg">
                    <p className="text-sm text-gray-400 mb-2">Link Anteprima (condividi per review):</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-2 bg-gray-900 rounded text-green-400 text-sm overflow-x-auto">
                        {window.location.origin}{importResult.preview_url}
                      </code>
                      <Button size="sm" variant="outline" onClick={copyPreviewLink}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" asChild>
                        <a href={importResult.preview_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button asChild variant="outline">
                      <a href={`/admin/events`}>Vai agli Eventi</a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Preview */}
            {preview && !importResult && (
              <div className="space-y-6">
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Eye className="w-5 h-5" />
                      Anteprima Dati Estratti
                    </CardTitle>
                    <CardDescription>
                      Verifica i dati prima di procedere con l'import
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Basic Info */}
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold text-white">Informazioni Base</h3>
                      <div className="grid gap-3">
                        <div className="flex items-center gap-2 text-gray-300">
                          <span className="font-medium">Titolo:</span>
                          <span className="text-white">{preview.title}</span>
                        </div>
                        {preview.subtitle && (
                          <div className="flex items-center gap-2 text-gray-300">
                            <span className="font-medium">Sottotitolo:</span>
                            <span className="text-white">{preview.subtitle}</span>
                          </div>
                        )}
                        {preview.date && (
                          <div className="flex items-center gap-2 text-gray-300">
                            <Calendar className="w-4 h-4" />
                            <span>{preview.date}</span>
                          </div>
                        )}
                        {preview.location && (
                          <div className="flex items-center gap-2 text-gray-300">
                            <MapPin className="w-4 h-4" />
                            <span>{preview.venue_name || preview.location}</span>
                            {preview.city && <Badge variant="secondary">{preview.city}</Badge>}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    {preview.description && (
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-white">Descrizione</h3>
                        <p className="text-gray-300 text-sm line-clamp-4">{preview.description}</p>
                      </div>
                    )}

                    {/* Hero Image */}
                    {preview.hero_image && (
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                          <ImageIcon className="w-4 h-4" />
                          Hero Image
                        </h3>
                        <img 
                          src={preview.hero_image} 
                          alt="Hero" 
                          className="max-w-md rounded-lg border border-gray-600"
                        />
                      </div>
                    )}

                    {/* Stats */}
                    {preview.stats?.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-white">Statistiche</h3>
                        <div className="flex flex-wrap gap-2">
                          {preview.stats.map((stat, i) => (
                            <Badge key={`stat-${s.label || i}`} variant="secondary" className="text-sm">
                              {stat.value} {stat.label}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tracks */}
                    {preview.tracks?.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-white">Track</h3>
                        <div className="flex flex-wrap gap-2">
                          {preview.tracks.map((track, i) => (
                            <Badge key={`track-${track}`} className="bg-purple-600">{track}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sessions */}
                    {preview.sessions?.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Sessioni ({preview.sessions.length})
                        </h3>
                        <div className="max-h-60 overflow-y-auto space-y-2">
                          {preview.sessions.slice(0, 10).map((session, i) => (
                            <div key={`session-${session.title}-${session.start_time || i}`} className="p-2 bg-gray-900 rounded text-sm">
                              <div className="flex items-center gap-2">
                                {session.start_time && (
                                  <span className="text-primary font-mono">{session.start_time}</span>
                                )}
                                <span className="text-white">{session.title}</span>
                              </div>
                              {session.speakers?.length > 0 && (
                                <div className="text-gray-400 text-xs mt-1">
                                  Con: {session.speakers.map(s => s.name).join(', ')}
                                </div>
                              )}
                            </div>
                          ))}
                          {preview.sessions.length > 10 && (
                            <p className="text-gray-400 text-sm">...e altre {preview.sessions.length - 10} sessioni</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Speakers */}
                    {preview.speakers?.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Speaker ({preview.speakers.length})
                        </h3>
                        <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                          {preview.speakers.map((speaker, i) => (
                            <div key={`speaker-${speaker.name || i}`} className="flex items-center gap-2 p-2 bg-gray-900 rounded">
                              {speaker.photo_url && (
                                <img 
                                  src={speaker.photo_url} 
                                  alt={speaker.name}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              )}
                              <div>
                                <p className="text-white text-sm">{speaker.name}</p>
                                {speaker.role && <p className="text-gray-400 text-xs">{speaker.role}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sponsors */}
                    {preview.sponsors?.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-white">Sponsor ({preview.sponsors.length})</h3>
                        <div className="flex flex-wrap gap-2">
                          {preview.sponsors.map((sponsor, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {sponsor.tier}: {sponsor.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Gallery */}
                    {preview.gallery_images?.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-white">
                          Gallery ({preview.gallery_images.length} immagini)
                        </h3>
                        <div className="flex gap-2 overflow-x-auto pb-2">
                          {preview.gallery_images.slice(0, 6).map((img, i) => (
                            <img 
                              key={`gallery-${img.slice(-20)}`}
                              src={img}
                              alt={`Gallery ${i + 1}`}
                              className="w-24 h-24 object-cover rounded border border-gray-600 flex-shrink-0"
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Action */}
                <Card className="bg-yellow-900/20 border-yellow-700">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-yellow-200">
                          L'evento verrà creato in stato <strong>BOZZA</strong> e non sarà visibile pubblicamente. 
                          Riceverai un link di anteprima per la review.
                        </p>
                        <p className="text-yellow-200/70 text-sm mt-2">
                          Verrà creato anche un redirect automatico dalla vecchia URL a quella nuova.
                        </p>
                      </div>
                      <Button
                        onClick={handleImport}
                        disabled={loading}
                        className="bg-primary hover:bg-primary/90"
                      >
                        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                        Conferma Import
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Import Agenda Tab */}
          <TabsContent value="agenda" className="space-y-6 mt-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <ListMusic className="w-5 h-5" />
                  Import Agenda su Evento Esistente
                </CardTitle>
                <CardDescription>
                  Importa solo l'agenda (track e sessioni) da un URL esterno su un evento già creato
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm text-gray-300 font-medium">Evento di destinazione *</label>
                  <Select value={agendaEventId} onValueChange={setAgendaEventId}>
                    <SelectTrigger className="bg-gray-900 border-gray-600" data-testid="agenda-event-select">
                      <SelectValue placeholder="Seleziona l'evento..." />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-600">
                      {events.map((ev) => (
                        <SelectItem key={ev.id} value={ev.id}>
                          <div className="flex items-center gap-2">
                            <span>{ev.title}</span>
                            {ev.date && <span className="text-muted-foreground text-xs">({ev.date})</span>}
                            <Badge variant={ev.status === 'publish' ? 'default' : 'secondary'} className="text-xs ml-1">
                              {ev.status === 'publish' ? 'Pub' : 'Bozza'}
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-gray-300 font-medium">URL Sorgente Agenda *</label>
                  <Input
                    value={agendaUrl}
                    onChange={(e) => setAgendaUrl(e.target.value)}
                    placeholder="https://edizioniprecedenti.startupitaliaopensummit.eu/2025/winter-2025/"
                    className="bg-gray-900 border-gray-600 text-white"
                    data-testid="agenda-url-input"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleAgendaPreview}
                    disabled={agendaLoading || !agendaUrl || !agendaEventId}
                    variant="outline"
                    className="border-gray-600"
                    data-testid="agenda-preview-btn"
                  >
                    {agendaLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                    Anteprima
                  </Button>
                  <Button
                    onClick={handleAgendaImport}
                    disabled={agendaLoading || !agendaUrl || !agendaEventId}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="agenda-import-btn"
                  >
                    {agendaLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    Importa Agenda
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Agenda Import Result */}
            {agendaResult && (
              <Card className="bg-green-900/20 border-green-700">
                <CardHeader>
                  <CardTitle className="text-green-400 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Agenda Importata
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-gray-800 rounded-lg">
                      <p className="text-2xl font-bold text-white">{agendaResult.tracks_created}</p>
                      <p className="text-sm text-gray-400">Track create</p>
                    </div>
                    <div className="text-center p-3 bg-gray-800 rounded-lg">
                      <p className="text-2xl font-bold text-white">{agendaResult.sessions_created}</p>
                      <p className="text-sm text-gray-400">Sessioni importate</p>
                    </div>
                    <div className="text-center p-3 bg-gray-800 rounded-lg">
                      <p className="text-2xl font-bold text-white">{agendaResult.speakers_created || 0}</p>
                      <p className="text-sm text-gray-400">Speaker creati</p>
                    </div>
                    <div className="text-center p-3 bg-gray-800 rounded-lg">
                      <p className="text-2xl font-bold text-white">{agendaResult.sessions_existing}</p>
                      <p className="text-sm text-gray-400">Sessioni pre-esistenti</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Button asChild variant="outline">
                      <a href="/admin/events">Vai all'evento</a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Agenda Preview */}
            {agendaPreview && !agendaResult && (
              <div className="space-y-4">
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Eye className="w-5 h-5" />
                      Anteprima Agenda
                    </CardTitle>
                    <CardDescription>
                      Evento: <span className="text-white font-medium">{agendaPreview.event_title}</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Tracks */}
                    {agendaPreview.tracks?.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-white">Track ({agendaPreview.tracks.length})</h3>
                        <div className="flex flex-wrap gap-2">
                          {agendaPreview.tracks.map((track, i) => (
                            <Badge key={`track-${track}`} className="bg-purple-600">{track}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sessions grouped by track */}
                    {agendaPreview.sessions?.length > 0 && (
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Sessioni ({agendaPreview.sessions.length})
                        </h3>
                        {agendaPreview.tracks.map((trackName) => {
                          const trackSessions = agendaPreview.sessions.filter(s => s.track === trackName);
                          if (trackSessions.length === 0) return null;
                          return (
                            <div key={trackName} className="space-y-1">
                              <p className="text-xs font-medium text-primary">{trackName} ({trackSessions.length})</p>
                              <div className="max-h-48 overflow-y-auto space-y-1">
                                {trackSessions.map((session, i) => (
                                  <div key={`agenda-${session.title}-${session.start_time || i}`} className="p-2 bg-gray-900 rounded text-sm flex items-center gap-2">
                                    {session.start_time && (
                                      <span className="text-primary font-mono text-xs w-24 flex-shrink-0">{session.start_time} - {session.end_time || '?'}</span>
                                    )}
                                    <span className="text-white truncate">{session.title}</span>
                                    {session.room && <span className="text-muted-foreground text-xs flex-shrink-0">[{session.room}]</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-yellow-900/20 border-yellow-700">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-yellow-200">
                          Le sessioni verranno aggiunte all'evento <strong>{agendaPreview.event_title}</strong>.
                          Le track necessarie verranno create automaticamente.
                        </p>
                      </div>
                      <Button
                        onClick={handleAgendaImport}
                        disabled={agendaLoading}
                        className="bg-primary hover:bg-primary/90"
                      >
                        {agendaLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                        Conferma Import
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="redirects" className="space-y-6 mt-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Gestione Redirect</CardTitle>
                <CardDescription>
                  Mappatura degli URL vecchi verso quelli nuovi (redirect 301)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingRedirects ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : redirects.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">Nessun redirect configurato</p>
                ) : (
                  <div className="space-y-3">
                    {redirects.map((redirect) => (
                      <div 
                        key={redirect.id}
                        className={`p-4 rounded-lg border ${redirect.is_active ? 'bg-gray-900 border-gray-600' : 'bg-gray-900/50 border-gray-700'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant={redirect.is_active ? 'default' : 'secondary'}>
                                {redirect.redirect_type}
                              </Badge>
                              <Badge variant={redirect.is_active ? 'outline' : 'secondary'}>
                                {redirect.is_active ? 'Attivo' : 'Disattivato'}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-400 truncate">
                              <span className="font-medium">Da:</span> {redirect.old_url}
                            </p>
                            <p className="text-sm text-white truncate">
                              <span className="font-medium text-gray-400">A:</span> {redirect.new_url}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => toggleRedirect(redirect.id)}
                            >
                              {redirect.is_active ? (
                                <ToggleRight className="w-5 h-5 text-green-400" />
                              ) : (
                                <ToggleLeft className="w-5 h-5 text-gray-400" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteRedirect(redirect.id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
  );
}
